import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { CheckCircle, RotateCcw, ImageIcon } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';
// Stickers stored with either a full https Supabase URL or a legacy /uploads/…
// path. resolveMediaUrl handles both correctly (leaves absolute URLs alone,
// prefixes legacy paths with the API base).
const getStickerUrl = (src) => resolveMediaUrl(src) || '';

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(seededRandom(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const FindStickerElement = ({
  element,
  isEditor = false,
  studentView = false,
  // Controlled-state props for lesson sync. When `foundIds` is a non-null
  // array, the element is in controlled mode: click handling still runs
  // locally (for animation), but state advances via `onFound`/`onRetry`
  // instead of internal setState. `disabled` blocks clicks entirely so the
  // teacher's mirrored view is read-only.
  foundIds: controlledFoundIds = null,
  idx: controlledIdx = null,
  onFound = null,
  onRetry: onRetryProp = null,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { content, id } = element;
  const {
    imageUrl = '',
    stickers = [],
    shuffleTargets = true,
    allowRetry = false,
    cropRect = null,
    fontFamily = 'Inter',
    fontSize = 22,
    textColor = '#ffffff',
    wordBgColor = '#2A4D25',
  } = content || {};

  const hasCrop = cropRect && cropRect.w > 0 && cropRect.h > 0 &&
    !(cropRect.x === 0 && cropRect.y === 0 && cropRect.w === 100 && cropRect.h === 100);

  // Every placed sticker (with position + src) — shown on the canvas so the
  // teacher can preview and drag it around even before typing a word.
  const displayStickers = useMemo(
    () => stickers.filter(
      (s) => s.src && typeof s.x === 'number' && typeof s.y === 'number'
    ),
    [stickers]
  );
  // Playable subset — stickers with a word actually participate in the game.
  const validStickers = useMemo(
    () => displayStickers.filter((s) => (s.word || '').trim()),
    [displayStickers]
  );

  // Group stickers by normalized word. If two stickers share the same word,
  // they collapse to a single prompt — clicking either counts as correct.
  const wordGroups = useMemo(() => {
    const map = new Map();
    validStickers.forEach((s) => {
      const key = (s.word || '').trim().toLowerCase();
      if (!key) return;
      if (!map.has(key)) map.set(key, { word: s.word.trim(), ids: [] });
      map.get(key).ids.push(s.id);
    });
    return Array.from(map.values());
  }, [validStickers]);

  const order = useMemo(() => {
    const words = wordGroups.map((g) => g.word);
    if (isEditor || !shuffleTargets) return words;
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 1;
    return shuffleWithSeed(words, seed);
  }, [wordGroups, shuffleTargets, id, isEditor]);

  const isControlled = Array.isArray(controlledFoundIds);
  const [localIdx, setLocalIdx] = useState(0);
  const [localFoundIds, setLocalFoundIds] = useState([]);
  const idx = isControlled ? (controlledIdx ?? 0) : localIdx;
  const foundIds = isControlled ? controlledFoundIds : localFoundIds;
  // Sticker id currently playing the "correct pick" celebrate-then-vanish
  // animation. Set on a correct click and cleared once the animation finishes.
  const [celebratingId, setCelebratingId] = useState(null);
  // True for ~400ms after a wrong click — shakes the prompt word red so the
  // student sees their click registered without covering the image.
  const [wrongFlash, setWrongFlash] = useState(false);
  const clickTimer = useRef(null);
  const wrongTimer = useRef(null);

  const orderKey = order.join('|');
  useEffect(() => {
    if (!isControlled) {
      setLocalIdx(0);
      setLocalFoundIds([]);
    }
    setCelebratingId(null);
    setWrongFlash(false);
  }, [orderKey, isControlled]);

  useEffect(() => () => {
    clearTimeout(clickTimer.current);
    clearTimeout(wrongTimer.current);
  }, []);

  const currentWord = order[idx];
  const currentGroup = wordGroups.find((g) => g.word === currentWord) || null;
  const done = !isEditor && order.length > 0 && idx >= order.length;

  const [naturalSize, setNaturalSize] = useState(null);
  const [containerSize, setContainerSize] = useState(null);
  const containerRef = useRef(null);
  const loaderRef = useRef(null);

  useEffect(() => { setNaturalSize(null); }, [imageUrl]);

  useEffect(() => {
    const img = loaderRef.current;
    if (img && img.complete && img.naturalWidth && img.naturalHeight) {
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    }
  }, [imageUrl]);

  // Re-runs when imageUrl transitions from '' to a URL — the initial early
  // return skips this ref, so we must re-attach the observer once the real
  // container mounts. Otherwise paintedBox stays null and stickers never
  // render even though the base image is visible.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const apply = (w, h) => {
      if (w > 0 && h > 0) setContainerSize({ w, h });
    };
    apply(el.offsetWidth, el.offsetHeight);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) apply(e.contentRect.width, e.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [imageUrl]);

  const paintedBox = useMemo(() => {
    if (!containerSize || !naturalSize) return null;
    const cropW = hasCrop ? cropRect.w : 100;
    const cropH = hasCrop ? cropRect.h : 100;
    const visibleAspect = (naturalSize.w * cropW) / (naturalSize.h * cropH);
    if (!isFinite(visibleAspect) || visibleAspect <= 0) return null;
    const containerAspect = containerSize.w / containerSize.h;
    let w, h;
    if (visibleAspect > containerAspect) {
      w = containerSize.w;
      h = w / visibleAspect;
    } else {
      h = containerSize.h;
      w = h * visibleAspect;
    }
    return {
      w, h,
      left: (containerSize.w - w) / 2,
      top: (containerSize.h - h) / 2,
    };
  }, [containerSize, naturalSize, hasCrop, cropRect]);

  // Click test: rectangular hit-test against each remaining sticker's bounding
  // box. A click on any sticker whose id belongs to the current word group
  // counts as correct — this both matches teacher's dedup expectation and
  // makes duplicate-word stickers get consumed together.
  const handleImageClick = useCallback((e) => {
    if (isEditor || disabled || done || !currentGroup || !paintedBox || celebratingId) return;
    const box = e.currentTarget.getBoundingClientRect();
    const cxPct = ((e.clientX - box.left) / box.width) * 100;
    const cyPct = ((e.clientY - box.top) / box.height) * 100;

    // Walk visible stickers in reverse so the topmost wins on overlap.
    const remaining = validStickers.filter((s) => !foundIds.includes(s.id));
    let clickedId = null;
    for (let i = remaining.length - 1; i >= 0; i -= 1) {
      const s = remaining[i];
      const halfW = (s.size || 12) / 2;
      const aspect = s.aspect || 1;
      const halfHpct = ((s.size || 12) / aspect) * (box.width / box.height) / 2;
      if (
        cxPct >= s.x - halfW && cxPct <= s.x + halfW &&
        cyPct >= s.y - halfHpct && cyPct <= s.y + halfHpct
      ) {
        clickedId = s.id;
        break;
      }
    }

    if (clickedId && currentGroup.ids.includes(clickedId)) {
      // Pop-then-vanish on the clicked sticker. After the animation, mark all
      // group members as found and advance. Duration must match the CSS
      // `fs-celebrate` keyframes below.
      setCelebratingId(clickedId);
      clearTimeout(clickTimer.current);
      const nextFoundIds = [...foundIds, ...currentGroup.ids];
      const nextIdx = idx + 1;
      clickTimer.current = setTimeout(() => {
        setCelebratingId(null);
        if (isControlled) {
          if (onFound) onFound({ foundIds: nextFoundIds, idx: nextIdx });
        } else {
          setLocalFoundIds(nextFoundIds);
          setLocalIdx(nextIdx);
        }
      }, 650);
    } else {
      setWrongFlash(true);
      clearTimeout(wrongTimer.current);
      wrongTimer.current = setTimeout(() => setWrongFlash(false), 450);
    }
  }, [isEditor, disabled, done, currentGroup, paintedBox, celebratingId, validStickers, foundIds, idx, isControlled, onFound]);

  const handleRetry = () => {
    if (isControlled) {
      if (onRetryProp) onRetryProp();
    } else {
      setLocalIdx(0);
      setLocalFoundIds([]);
    }
    setCelebratingId(null);
    setWrongFlash(false);
  };

  if (!imageUrl) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300">
        <ImageIcon size={40} className="text-gray-300 mb-2" />
        <p className="text-sm text-gray-400">{t('games.dragLabel.uploadImageInSettings')}</p>
      </div>
    );
  }

  const hideChrome = !isEditor;
  const promptWord = isEditor
    ? (wordGroups[0]?.word || t('games.findObjectPlayer.find'))
    : (currentWord || '');

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden flex flex-col ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{ borderColor: '#e5e7eb' }}
    >
      <style>{`
        @keyframes fs-word-pop {
          0% { transform: scale(0.85); opacity: 0; }
          60% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        /* Correct-pick celebration on the clicked sticker: pop up, settle,
           then fade out. Total duration ~650ms — match the setTimeout above. */
        @keyframes fs-celebrate {
          0%   { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
          35%  { transform: translate(-50%, -50%) scale(1.35); opacity: 1; }
          60%  { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1);    opacity: 0; }
        }
        /* Wrong-pick shake on the prompt bubble. */
        @keyframes fs-shake {
          0%, 100% { transform: translateX(0); }
          20%      { transform: translateX(-6px); }
          40%      { transform: translateX(6px); }
          60%      { transform: translateX(-4px); }
          80%      { transform: translateX(4px); }
        }
        .fs-word { animation: fs-word-pop 0.35s cubic-bezier(.2,.8,.2,1.4); }
        .fs-celebrate { animation: fs-celebrate 0.65s cubic-bezier(.2,.8,.2,1.4) forwards; }
        .fs-shake { animation: fs-shake 0.4s ease-in-out; }
      `}</style>

      <div
        className="flex items-center justify-center px-3 py-2 bg-gray-50/80"
        style={{ minHeight: 56 }}
      >
        {!done && promptWord && (
          <div
            key={currentWord || 'editor-preview'}
            className={`fs-word inline-block px-4 py-1 rounded-full font-bold shadow-md ${wrongFlash ? 'fs-shake' : ''}`}
            style={{
              backgroundColor: wrongFlash ? '#ef4444' : wordBgColor,
              color: textColor,
              fontFamily,
              fontSize: `${fontSize}px`,
              transition: wrongFlash ? 'none' : 'background-color 300ms ease-out',
            }}
          >
            {promptWord}
          </div>
        )}
        {done && (
          <div className="flex items-center gap-1.5 text-emerald-700 text-sm font-medium">
            <CheckCircle size={16} />
            {t('games.findObjectPlayer.allFound', { defaultValue: 'Усі знайдено!' })}
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        className="relative flex-1 min-h-0 min-w-0 overflow-hidden"
      >
        <img
          ref={loaderRef}
          src={getMediaUrl(imageUrl)}
          alt=""
          draggable={false}
          onLoad={(e) => {
            const w = e.currentTarget.naturalWidth;
            const h = e.currentTarget.naturalHeight;
            if (w && h) setNaturalSize({ w, h });
          }}
          className="absolute inset-0 w-full h-full pointer-events-none select-none"
          style={{
            objectFit: 'contain',
            objectPosition: 'center',
            opacity: hasCrop ? 0 : 1,
          }}
        />

        {paintedBox && (
          <div
            onClick={handleImageClick}
            className="absolute overflow-hidden select-none"
            style={{
              left: `${paintedBox.left}px`,
              top: `${paintedBox.top}px`,
              width: `${paintedBox.w}px`,
              height: `${paintedBox.h}px`,
              cursor: !isEditor && !disabled && !done && currentGroup ? 'crosshair' : 'default',
            }}
          >
            {hasCrop && (
              <img
                src={getMediaUrl(imageUrl)}
                alt=""
                draggable={false}
                className="pointer-events-none select-none"
                style={{
                  position: 'absolute',
                  width: `${10000 / cropRect.w}%`,
                  height: `${10000 / cropRect.h}%`,
                  left: `${-cropRect.x * 100 / cropRect.w}%`,
                  top: `${-cropRect.y * 100 / cropRect.h}%`,
                  maxWidth: 'none',
                }}
              />
            )}

            {/* Stickers overlay. Height derived from width via aspect ratio so
                stickers keep their natural proportions. In the editor we render
                every placed sticker so the teacher can position it before
                typing a word. */}
            {displayStickers.map((s) => {
              const isFound = foundIds.includes(s.id);
              if (isFound) return null;
              const isCelebrating = celebratingId === s.id;
              const size = s.size || 12;
              const aspect = s.aspect || 1;
              const widthPct = size;
              const heightPct = (size / aspect) * (paintedBox.w / paintedBox.h);
              return (
                <img
                  key={s.id}
                  src={getStickerUrl(s.src)}
                  alt=""
                  draggable={false}
                  onError={(e) => {
                    // If the sticker URL is broken, surface it as a visible red
                    // placeholder box so the teacher notices instead of a silent
                    // invisible sticker.
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.4)';
                    e.currentTarget.style.outline = '2px dashed #ef4444';
                  }}
                  className={`absolute pointer-events-none select-none ${isCelebrating ? 'fs-celebrate' : ''}`}
                  style={{
                    left: `${s.x}%`,
                    top: `${s.y}%`,
                    width: `${widthPct}%`,
                    height: `${heightPct}%`,
                    transform: 'translate(-50%, -50%)',
                    objectFit: 'contain',
                  }}
                />
              );
            })}

            {/* Editor empty-state: image is set but no stickers placed yet. */}
            {isEditor && displayStickers.length === 0 && (
              <div className="absolute inset-x-0 bottom-3 flex justify-center pointer-events-none">
                <div className="px-3 py-1.5 rounded-lg bg-black/60 text-white text-xs font-medium shadow">
                  {t('games.findStickerPlayer.editorEmpty', { defaultValue: 'Натисни «Додати стікер» справа, щоб розставити стікери' })}
                </div>
              </div>
            )}
          </div>
        )}

        {!isEditor && !disabled && done && allowRetry && (
          <div className="absolute bottom-2 right-2 z-10">
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindStickerElement;
